import React from 'react';
import {
    BodyLargeEmphasized,
    BodySmallEmphasized,
    BodySmallRegular,
    LabelEmphasized,
    Separator,
    Sheet,
    View,
    XStack,
    YStack,
} from 'smoothie';

type GrantedParty =
    | 'You'
    | 'Your Guide'
    | 'Your partner'
    | 'Your partner*'
    | 'Fruitful tech';

const DetailSection = ({
    title,
    description,
    grantedParties,
    comingSoon = false,
    topSeparator = false,
}: {
    title: string;
    description: string;
    grantedParties: GrantedParty[];
    comingSoon?: boolean;
    topSeparator?: boolean;
}) => {
    const TopSeparator = () => (
        <Separator width="100%" borderColor="$gray700" marginVertical={12} />
    );
    const BottomSeparator = () => (
        <Separator width="100%" borderColor="$gray700" marginTop={20} />
    );
    return (
        <YStack width="100%">
            {topSeparator && <TopSeparator />}
            <XStack justifyContent="space-between" marginBottom={3}>
                <BodyLargeEmphasized>{title}</BodyLargeEmphasized>
                {comingSoon && (
                    <View
                        paddingTop={4}
                        paddingHorizontal={12}
                        borderRadius={100}
                        backgroundColor="$gray100"
                    >
                        <LabelEmphasized color="$gray900">
                            Coming soon
                        </LabelEmphasized>
                    </View>
                )}
            </XStack>
            <BodySmallRegular color="$gray1100">{description}</BodySmallRegular>
            {grantedParties.length > 0 && (
                <XStack
                    marginTop={12}
                    gap={8}
                    flexWrap="wrap"
                    separator={
                        <BodySmallEmphasized>&bull;</BodySmallEmphasized>
                    }
                >
                    {grantedParties.map((party) => (
                        <BodySmallEmphasized key={party}>
                            {party}
                        </BodySmallEmphasized>
                    ))}
                </XStack>
            )}
            <BottomSeparator />
        </YStack>
    );
};

interface AboutInstitutionLinkingSheetProps {
    isOpen: boolean;
    onClose: () => void;
    isPartnerMembership: boolean;
}

const AboutInstitutionLinkingSheet = ({
    isPartnerMembership,
    isOpen,
    onClose,
}: AboutInstitutionLinkingSheetProps) => {
    return (
        <Sheet
            title="Who can see your data"
            showCloseButton={true}
            isOpen={isOpen}
            onClose={onClose}
            forceKeyboardCompatibleBottomSheet
        >
            <YStack width="100%" gap={12}>
                <LabelEmphasized textAlign="center">
                    Fruitful does not see or collect your log-in credentials for
                    external institutions.
                </LabelEmphasized>
                <DetailSection
                    topSeparator
                    title="Account summary"
                    description="Including balances and account owners"
                    grantedParties={
                        isPartnerMembership
                            ? [
                                  'You',
                                  'Your partner*',
                                  'Your Guide',
                                  'Fruitful tech',
                              ]
                            : ['You', 'Your Guide', 'Fruitful tech']
                    }
                />
                <DetailSection
                    title="Account details"
                    description="Including interest rates, credit limits, minimum payments"
                    grantedParties={
                        isPartnerMembership
                            ? [
                                  'You',
                                  'Your partner*',
                                  'Your Guide',
                                  'Fruitful tech',
                              ]
                            : ['You', 'Your Guide', 'Fruitful tech']
                    }
                />
                <DetailSection
                    title="Investment details"
                    description="Including holdings and allocations for Fruitful investment accounts"
                    grantedParties={
                        isPartnerMembership
                            ? [
                                  'You',
                                  'Your partner*',
                                  'Your Guide',
                                  'Fruitful tech',
                              ]
                            : ['You', 'Your Guide', 'Fruitful tech']
                    }
                />
                <DetailSection
                    title="Transactions"
                    description="Including individual purchases, transfers, and payments"
                    grantedParties={
                        isPartnerMembership
                            ? ['You', 'Your partner*', 'Fruitful tech']
                            : ['You', 'Fruitful tech']
                    }
                />
                {isPartnerMembership && (
                    <BodySmallRegular color="$gray900" marginTop={-6}>
                        *Partners will only be able to see details for joint
                        Fruitful accounts and external accounts they link to
                        Fruitful.
                    </BodySmallRegular>
                )}
            </YStack>
        </Sheet>
    );
};

export default AboutInstitutionLinkingSheet;
